import { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowLeftRight, History, Layers, Users } from 'lucide-react'
import { eventMagnitude } from './data'
import { EventRow, RankDelta, ScoreDelta, TeamLogo, formatLongDay } from './ui'

const FILTERS = [
  { id: 'all', label: 'Everything', icon: History },
  { id: 'roster', label: 'Roster moves', icon: ArrowLeftRight },
  { id: 'tier', label: 'Re-rankings', icon: Layers },
  { id: 'league', label: 'League shifts', icon: Users },
]

const matchesFilter = (change, filter) => {
  switch (filter) {
    case 'roster': return change.events.some(e => e.kind === 'in' || e.kind === 'out')
    case 'tier': return change.events.some(e => e.kind === 'tier')
    case 'league': return change.events.length === 0 && change.rankDelta !== 0
    default: return true
  }
}

function ContextLine({ change }) {
  const { passedBy, passed, events, scoreDelta, tracked, rankDelta } = change
  const parts = []
  if (!events.length && Math.abs(scoreDelta) > 1e-6) {
    parts.push(
      <p key="unknown" className="hv-context hv-muted">
        {tracked ? 'Score changed.' : 'Score changed, but the reasons were not recorded (before roster tracking).'}
      </p>
    )
  }
  if (!events.length && Math.abs(scoreDelta) < 1e-6 && rankDelta !== 0) {
    parts.push(
      <p key="nochange" className="hv-context hv-muted">
        Nothing changed on this roster. The rank moved because of other teams.
      </p>
    )
  }
  const group = (label, teams) =>
    teams.length > 0 && (
      <div key={label} className="hv-context-teams">
        <span className="hv-muted hv-small">{label}</span>
        {teams.slice(0, 8).map(t => (
          <span key={t.id} className="hv-team-pill" title={t.name}>
            <TeamLogo team={t} size={14} /> {t.acronym}
          </span>
        ))}
        {teams.length > 8 && <span className="hv-muted hv-small">+{teams.length - 8}</span>}
      </div>
    )
  parts.push(group('Moved past', passed))
  parts.push(group('Passed by', passedBy))
  return <>{parts}</>
}

function ChangeCard({ change, league, active, onSelect }) {
  const ref = useRef(null)
  useEffect(() => {
    // Scroll only the feed (positioned, so it is the offsetParent), never the page.
    const el = ref.current
    if (active && el?.offsetParent) el.offsetParent.scrollTo({ top: el.offsetTop - 8, behavior: 'smooth' })
  }, [active])
  const events = [...change.events].sort((a, b) => eventMagnitude(b) - eventMagnitude(a))
  const maxImpact = Math.max(...events.map(eventMagnitude), 0.0001)

  return (
    <motion.article
      ref={ref}
      layout="position"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className={`hv-change ${active ? 'is-active' : ''}`}
      onClick={() => onSelect(change.date)}
    >
      <header className="hv-change-head">
        <time className="hv-change-date">{formatLongDay(change.date)}</time>
        <div className="hv-change-summary">
          <span className="hv-rank-move">
            #{change.prevRank} <span className="hv-arrow">→</span> <b>#{change.rank}</b>
          </span>
          <RankDelta value={change.rankDelta} />
          <ScoreDelta value={change.scoreDelta} />
        </div>
      </header>
      {events.length > 0 && (
        <ul className="hv-events">
          {events.map((e, i) => (
            <EventRow key={`${e.player_id || e.name}-${i}`} event={e} league={league} maxImpact={maxImpact} />
          ))}
        </ul>
      )}
      <ContextLine change={change} />
    </motion.article>
  )
}

export default function ChangeFeed({ league, changes, pinnedDate, onPinDate }) {
  const [filter, setFilter] = useState('all')
  const counts = useMemo(
    () => Object.fromEntries(FILTERS.map(f => [f.id, changes.filter(c => matchesFilter(c, f.id)).length])),
    [changes]
  )
  const visible = changes.filter(c => matchesFilter(c, filter))

  return (
    <section className="hv-card hv-feed">
      <header className="hv-card-head">
        <div>
          <h3>What changed</h3>
          <p className="hv-muted hv-small">Every update that moved this team, newest first. Impact is each change's effect on the score.</p>
        </div>
      </header>
      <div className="hv-segmented hv-segmented--wrap" role="tablist">
        {FILTERS.map(f => (
          <button
            key={f.id}
            role="tab"
            aria-selected={filter === f.id}
            className={filter === f.id ? 'is-active' : ''}
            onClick={() => setFilter(f.id)}
          >
            <f.icon size={14} /> {f.label} <span className="hv-count">{counts[f.id]}</span>
          </button>
        ))}
      </div>
      <div className="hv-feed-list">
        <AnimatePresence initial={false}>
          {visible.map(c => (
            <ChangeCard key={c.date} change={c} league={league} active={c.date === pinnedDate} onSelect={onPinDate} />
          ))}
        </AnimatePresence>
        {!visible.length && <p className="hv-empty">Nothing in this period.</p>}
      </div>
    </section>
  )
}
